import api from './api';
import { Campaign, ApiResponse, PaginatedResponse } from '../types';

interface CampaignFilters {
  page?: number;
  limit?: number;
  status?: string;
  platform?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  created_after?: string;
  created_before?: string;
}

interface CampaignMetrics {
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
  ctr: number;
  cpc: number;
  daily: Array<{
    date: string;
    impressions: number;
    clicks: number;
    spend: number;
    conversions: number;
  }>;
}

export interface GoogleRecommendation {
  type: string;
  label: string;
  hint: string;
  level: 'campaign' | 'ad_group' | 'ad' | 'account';
  resourceName: string;
  campaign: string | null;
  adGroup: string | null;
  dismissed: boolean;
  impact: {
    base: { impressions: number; clicks: number; cost: number; conversions: number };
    potential: { impressions: number; clicks: number; cost: number; conversions: number };
  };
}

export interface GoogleCampaignSettings {
  biddingStrategyType: string | null;
  biddingStrategyLabel: string | null;
  targetCpa: number | null;
  targetSpend: number | null;
  targetRoas: number | null;
  optimizationScore: number | null;
  avgCpa: number | null;
  avgCpc: number | null;
  aiBidding: boolean;
  usesSelectiveOptimization?: boolean;
  conversionGoals: Array<{ id: string; resourceName?: string; name: string; category: string; categoryLabel: string; biddable: boolean; selected: boolean }>;
}

export interface ConversionSnippet {
  id: string;
  name: string;
  category: string;
  type: string;
  snippets: Array<{ type: string; format: string; globalSiteTag: string; eventSnippet: string }>;
  primary: { type: string; format: string; globalSiteTag: string; eventSnippet: string } | null;
}

export const campaignService = {
  async getCampaigns(filters?: CampaignFilters): Promise<PaginatedResponse<Campaign>> {
    const response = await api.get('/campaigns', { params: filters });
    return response.data;
  },

  async getCampaign(id: string): Promise<ApiResponse<Campaign>> {
    const response = await api.get(`/campaigns/${id}`);
    return response.data;
  },

  async createCampaign(data: Partial<Campaign>): Promise<ApiResponse<Campaign>> {
    const response = await api.post('/campaigns', data);
    return response.data;
  },

  async updateCampaign(id: string, data: Partial<Campaign>, onProgress?: (p: number, s: string) => void): Promise<ApiResponse<Campaign>> {
    const response = await api.put(`/campaigns/${id}`, data);
    const body: any = response.data;

    // Creative sync com vídeo vai pra background — aguarda o job via pollJobUntilComplete
    // (mesmo padrão do image studio, igual o resto da plataforma).
    const creativeJob = body?.syncResults?.creativeSync;
    // eslint-disable-next-line no-console
    console.log('[updateCampaign] creativeJob from backend:', creativeJob);
    if (creativeJob?.async && creativeJob?.jobId) {
      // eslint-disable-next-line no-console
      console.log(`[updateCampaign] ⏳ polling job ${creativeJob.jobId}...`);
      try {
        const { pollJobUntilComplete } = await import('./aiJobService');
        const jobResult = await pollJobUntilComplete(creativeJob.jobId, (p, s) => {
          // eslint-disable-next-line no-console
          console.log(`[updateCampaign] poll job=${creativeJob.jobId} progress=${p}% status=${s}`);
          onProgress?.(p, s);
        }, 2500, 600000); // 2.5s poll, 10min
        // eslint-disable-next-line no-console
        console.log(`[updateCampaign] ✅ job ${creativeJob.jobId} completed`, jobResult);
        // Normaliza o resultado pro mesmo shape do fluxo síncrono: merge dos resultados
        // por plataforma direto em syncResults pra o código de toast tratar igual.
        const inner = jobResult?.data || jobResult || {};
        if (inner.meta)     body.syncResults.meta     = { ...(body.syncResults.meta     || {}), creativeSync: inner.meta,     success: (body.syncResults.meta?.success     ?? true) && inner.meta.success     !== false };
        if (inner.google)   body.syncResults.google   = { ...(body.syncResults.google   || {}), creativeSync: inner.google,   success: (body.syncResults.google?.success   ?? true) && inner.google.success   !== false };
        if (inner.linkedin) body.syncResults.linkedin = { ...(body.syncResults.linkedin || {}), creativeSync: inner.linkedin, success: (body.syncResults.linkedin?.success ?? true) && inner.linkedin.success !== false };
        body.syncResults.creativeSync = { ...creativeJob, done: true, success: true, result: inner };
      } catch (err: any) {
        body.syncResults.creativeSync = { ...creativeJob, done: true, success: false, error: err?.message || 'Falha ao sincronizar criativos' };
      }
    }
    return body;
  },

  async deleteCampaign(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/campaigns/${id}`);
    return response.data;
  },

  async getCampaignsMetricsSummary(startDate: string, endDate: string): Promise<ApiResponse<{
    startDate: string;
    endDate: string;
    campaigns: Record<string, {
      impressions: number;
      clicks: number;
      spend: number;
      conversions: number;
      ctr: number;
      googleSpend: number;
      metaSpend: number;
      linkedinSpend: number;
      openaiSpend: number;
    }>;
  }>> {
    const response = await api.get('/campaigns/metrics-summary', {
      params: { startDate, endDate }
    });
    return response.data;
  },

  async getCampaignMetrics(id: string, startDate?: string, endDate?: string): Promise<ApiResponse<CampaignMetrics>> {
    const response = await api.get(`/campaigns/${id}/metrics`, {
      params: { startDate, endDate }
    });
    return response.data;
  },

  async duplicateCampaign(id: string): Promise<ApiResponse<Campaign>> {
    const response = await api.post(`/campaigns/${id}/duplicate`);
    return response.data;
  },

  // Native Google Ads recommendations (campaign / ad group / ad), pulled live from Google.
  async getGoogleRecommendations(id: string): Promise<ApiResponse<{
    items: GoogleRecommendation[];
    grouped: { campaign: GoogleRecommendation[]; ad_group: GoogleRecommendation[]; ad: GoogleRecommendation[]; account: GoogleRecommendation[] };
    customerId: string;
    scope: 'campaign' | 'account';
    count: number;
  }> & { code?: string }> {
    const response = await api.get(`/campaigns/${id}/google-recommendations`);
    return response.data;
  },
  async getGoogleSettings(id: string): Promise<ApiResponse<GoogleCampaignSettings> & { code?: string }> {
    const response = await api.get(`/campaigns/${id}/google-settings`);
    return response.data;
  },
  async updateGoogleBidding(id: string, payload: { strategy: string; targetCpa?: number | null; targetRoas?: number | null }): Promise<ApiResponse<any> & { code?: string }> {
    const response = await api.post(`/campaigns/${id}/google-bidding`, payload);
    return response.data;
  },
  async updateGoogleConversionGoals(id: string, categories: string[]): Promise<ApiResponse<any> & { code?: string }> {
    const response = await api.post(`/campaigns/${id}/google-conversion-goals`, { categories });
    return response.data;
  },
  async getConversionSnippet(id: string, actionId: string): Promise<ApiResponse<ConversionSnippet> & { code?: string }> {
    const response = await api.get(`/campaigns/${id}/conversion-snippet`, { params: { actionId } });
    return response.data;
  },
  async createConversion(id: string, payload: { name: string; category?: string }): Promise<ApiResponse<{ id: string | null; resourceName: string | null }> & { code?: string }> {
    const response = await api.post(`/campaigns/${id}/conversions`, payload);
    return response.data;
  },
  async applyGoogleRecommendation(id: string, resourceName: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/campaigns/${id}/google-recommendations/apply`, { resourceName });
    return response.data;
  },
  async dismissGoogleRecommendation(id: string, resourceName: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/campaigns/${id}/google-recommendations/dismiss`, { resourceName });
    return response.data;
  },

  async getCampaignPlatformStatus(id: string): Promise<ApiResponse<{
    fetchedAt: string;
    google: null | { error?: string; campaign?: { id: string; status: string; servingStatus: string }; ads?: Array<{ external_id: string; status: string; approvalStatus?: string; reviewStatus?: string; adStrength?: string }>; adsError?: string };
    meta: null | { error?: string; campaign?: { id: string; status: string; effectiveStatus: string; issues: any[] }; ads?: Array<{ external_id: string; status: string; effectiveStatus: string; issues?: any[]; reviewFeedback?: any; qualityRanking?: string; engagementRateRanking?: string; conversionRateRanking?: string }> };
    linkedin: null | { error?: string; campaign?: { id: string; status: string; servingStatuses: string[] }; ads?: Array<{ external_id: string; status: string; servingStatuses?: string[]; reviewStatus?: string }> };
  }>> {
    const response = await api.get(`/campaigns/${id}/platform-status`);
    return response.data;
  },

  // Sync campaigns from connected ad platforms (Google Ads, Meta Ads)
  async syncCampaigns(projectId: string): Promise<ApiResponse<{
    google: { synced: number; errors: string[] };
    meta: { synced: number; errors: string[] };
    total: number;
  }>> {
    const response = await api.post('/campaigns/sync', {}, {
      headers: { 'X-Project-Id': projectId }
    });
    return response.data;
  },

  // Push helpers — backend enfileira e retorna 202 { jobId }. Usamos o mesmo
  // pollJobUntilComplete já padronizado na plataforma (igual image studio,
  // competitor analysis, etc) pra manter consistência e loader/progress UX.
  async _enqueueAndPoll(
    url: string,
    projectId: string,
    onProgress?: (progress: number, state: string) => void
  ): Promise<ApiResponse<any>> {
    const { pollJobUntilComplete } = await import('./aiJobService');
    const enqueue = await api.post(url, {}, { headers: { 'X-Project-Id': projectId } });
    const body: any = enqueue.data || {};
    if (!body.async || !body.jobId) return body;
    const result = await pollJobUntilComplete(body.jobId, onProgress, 2500, 600000); // 2.5s poll, 10min timeout
    return result?.data || result || { success: true };
  },

  async pushToGoogleAds(projectId: string, campaignId: string, onProgress?: (p: number, s: string) => void): Promise<ApiResponse<any>> {
    return this._enqueueAndPoll(`/campaigns/${campaignId}/push/google`, projectId, onProgress);
  },
  async pushToMetaAds(projectId: string, campaignId: string, onProgress?: (p: number, s: string) => void): Promise<ApiResponse<any>> {
    return this._enqueueAndPoll(`/campaigns/${campaignId}/push/meta`, projectId, onProgress);
  },
  async pushToLinkedIn(projectId: string, campaignId: string, onProgress?: (p: number, s: string) => void): Promise<ApiResponse<any>> {
    return this._enqueueAndPoll(`/campaigns/${campaignId}/push/linkedin`, projectId, onProgress);
  },

  // Toggle campaign status on the ad platform (play/pause)
  async togglePlatformStatus(projectId: string, campaignId: string, status: 'active' | 'paused'): Promise<ApiResponse<any>> {
    const response = await api.post(`/campaigns/${campaignId}/toggle-status`, { status }, {
      headers: { 'X-Project-Id': projectId }
    });
    return response.data;
  },

  // Generate campaign data with AI
  async generateWithAI(
    prompt: string,
    platforms: string[],
    useAsync = true,
    opts?: { ignorePattern?: boolean; variationDirection?: string }
  ): Promise<ApiResponse<{
    name: string;
    description: string;
    type: string;
    objective: string;
    budget_type: string;
    budget_amount: number;
    audience: {
      age_min: number;
      age_max: number;
      genders: string[];
      locations: string[];
      interests: string[];
    };
    creatives: Array<{
      headlines: string[];
      descriptions: string[];
      ctaText: string;
      destinationUrl: string;
    }>;
    channels: string[];
    keywords?: string[];
    negativeKeywords?: string[];
  }> & { async?: boolean; jobId?: string }> {
    const response = await api.post('/campaigns/generate-with-ai', {
      prompt,
      platforms,
      async: useAsync,
      ignorePattern: opts?.ignorePattern,
      variationDirection: opts?.variationDirection
    });
    return response.data;
  },

  // ============ Campaign Keywords ============

  async getCampaignKeywords(campaignId: string, type?: 'all' | 'positive' | 'negative'): Promise<ApiResponse<CampaignKeyword[]>> {
    const response = await api.get(`/campaigns/${campaignId}/keywords`, { params: { type } });
    return response.data;
  },

  async addKeywordsToCampaign(
    campaignId: string,
    keywords: (string | KeywordInput)[],
    matchType?: 'exact' | 'phrase' | 'broad',
    opts?: { adGroupIds?: string[]; applyToAll?: boolean }
  ): Promise<ApiResponse<{ created: CampaignKeyword[]; errors: any[] }>> {
    const response = await api.post(`/campaigns/${campaignId}/keywords`, {
      keywords,
      matchType,
      adGroupIds: opts?.adGroupIds,
      applyToAll: opts?.applyToAll,
    });
    return response.data;
  },

  async getCampaignAdGroups(campaignId: string): Promise<ApiResponse<{ id: string; name: string; status?: string }[]>> {
    const response = await api.get(`/campaigns/${campaignId}/ad-groups`);
    return response.data;
  },

  async linkExistingKeywords(campaignId: string, keywordIds: string[], matchType?: 'exact' | 'phrase' | 'broad'): Promise<ApiResponse<CampaignKeyword[]>> {
    const response = await api.post(`/campaigns/${campaignId}/keywords/link`, { keywordIds, matchType });
    return response.data;
  },

  async updateCampaignKeyword(campaignId: string, keywordId: string, data: { matchType?: string; status?: string }): Promise<ApiResponse<CampaignKeyword>> {
    const response = await api.put(`/campaigns/${campaignId}/keywords/${keywordId}`, data);
    return response.data;
  },

  async removeKeywordFromCampaign(campaignId: string, keywordId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/campaigns/${campaignId}/keywords/${keywordId}`);
    return response.data;
  },

  async addNegativeKeywords(campaignId: string, keywords: (string | KeywordInput)[], matchType?: 'exact' | 'phrase' | 'broad'): Promise<ApiResponse<{ created: CampaignKeyword[]; errors: any[] }>> {
    const response = await api.post(`/campaigns/${campaignId}/negative-keywords`, { keywords, matchType });
    return response.data;
  },

  // Search Terms
  async getCampaignSearchTerms(campaignId: string, params?: { page?: number; limit?: number; startDate?: string; endDate?: string }): Promise<ApiResponse<SearchTermReport[]>> {
    const response = await api.get(`/campaigns/${campaignId}/search-terms`, { params });
    return response.data;
  },

  async addSearchTermAsKeyword(campaignId: string, data: { searchTermId?: string; searchTerm?: string; type: 'positive' | 'negative'; matchType?: 'exact' | 'phrase' | 'broad' }): Promise<ApiResponse<CampaignKeyword>> {
    const response = await api.post(`/campaigns/${campaignId}/search-terms/add-as-keyword`, data);
    return response.data;
  },

  async syncSearchTerms(campaignId: string): Promise<ApiResponse<void>> {
    const response = await api.post(`/campaigns/${campaignId}/search-terms/sync`);
    return response.data;
  },

  // Clicks
  async getCampaignClicks(campaignId: string, params?: { page?: number; limit?: number; startDate?: string; endDate?: string }): Promise<ApiResponse<ClickDetail[]>> {
    const response = await api.get(`/campaigns/${campaignId}/clicks`, { params });
    return response.data;
  },

  // ============ Campaign Audiences ============

  async getCampaignAudiences(campaignId: string): Promise<ApiResponse<CampaignAudienceAssociation[]>> {
    const response = await api.get(`/campaigns/${campaignId}/audiences`);
    return response.data;
  },

  async addAudienceToCampaign(campaignId: string, data: { audienceId: string; type?: 'include' | 'exclude'; bidAdjustment?: number; priority?: number }): Promise<ApiResponse<CampaignAudienceAssociation>> {
    const response = await api.post(`/campaigns/${campaignId}/audiences`, data);
    return response.data;
  },

  async updateCampaignAudience(campaignId: string, audienceId: string, data: { type?: 'include' | 'exclude'; bidAdjustment?: number; priority?: number }): Promise<ApiResponse<CampaignAudienceAssociation>> {
    const response = await api.put(`/campaigns/${campaignId}/audiences/${audienceId}`, data);
    return response.data;
  },

  async removeAudienceFromCampaign(campaignId: string, audienceId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/campaigns/${campaignId}/audiences/${audienceId}`);
    return response.data;
  },

  // ============ Platform Pixels ============

  async getPlatformPixels(projectId: string): Promise<ApiResponse<PlatformPixels>> {
    const response = await api.get('/campaigns/pixels/available', {
      headers: { 'X-Project-Id': projectId }
    });
    return response.data;
  },

  async savePlatformPixel(projectId: string, platform: string, pixelId: string | null, pixelName: string | null): Promise<ApiResponse<any>> {
    const response = await api.post('/campaigns/pixels/select', { platform, pixelId, pixelName }, {
      headers: { 'X-Project-Id': projectId }
    });
    return response.data;
  },

  async createPlatformPixel(
    projectId: string,
    platform: 'meta' | 'google' | 'linkedin',
    name: string,
    category?: string
  ): Promise<ApiResponse<{ id: string; name: string; platform: string; type?: string; category?: string }>> {
    const response = await api.post('/campaigns/pixels/create', { platform, name, category }, {
      headers: { 'X-Project-Id': projectId }
    });
    return response.data;
  },

  async deletePlatformPixel(
    projectId: string,
    platform: 'meta' | 'google' | 'linkedin',
    pixelId: string
  ): Promise<ApiResponse<{ success: boolean; platform: string; pixelId: string; action: string }>> {
    const response = await api.post('/campaigns/pixels/delete', { platform, pixelId }, {
      headers: { 'X-Project-Id': projectId }
    });
    return response.data;
  },

  async getPlatformPixelSnippet(
    projectId: string,
    platform: 'meta' | 'google' | 'linkedin',
    pixelId: string
  ): Promise<ApiResponse<{
    pixelId: string;
    platform: string;
    html: string;
    noscript?: string;
    conversionSnippet?: string;
    instructions: string;
    docsUrl?: string;
  }>> {
    const response = await api.get('/campaigns/pixels/snippet', {
      params: { platform, pixelId },
      headers: { 'X-Project-Id': projectId }
    });
    return response.data;
  },

  async getMetaInstagramAccounts(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/campaigns/meta-instagram-accounts');
    return response.data;
  },

  async getLinkedinOrganizations(): Promise<ApiResponse<Array<{
    organization_urn: string;
    name: string;
    logo_url?: string | null;
    vanity_name?: string | null;
    is_default?: boolean;
  }>>> {
    const response = await api.get('/campaigns/linkedin-organizations');
    return response.data;
  }
};

// Types for campaign keywords
export interface CampaignKeyword {
  id: string;
  keyword: string;
  match_type: 'exact' | 'phrase' | 'broad';
  is_negative?: boolean;
  search_volume?: number;
  difficulty?: number;
  cpc?: number;
  competition?: 'low' | 'medium' | 'high';
  status: 'active' | 'paused' | 'deleted';
  created_at: string;
}

export interface KeywordInput {
  keyword: string;
  matchType?: 'exact' | 'phrase' | 'broad';
  searchVolume?: number;
  difficulty?: number;
  cpc?: number;
}

export interface SearchTermReport {
  id: string;
  campaign_id: string;
  search_term: string;
  keyword_matched?: string;
  match_type: 'exact' | 'phrase' | 'broad' | 'unknown';
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  cpc: number;
  date: string;
  platform: 'google' | 'meta' | 'linkedin';
  added_as_negative: boolean;
  added_as_keyword: boolean;
}

export interface ClickDetail {
  id: string;
  campaign_id: string;
  ad_id?: string;
  click_time: string;
  device: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  browser?: string;
  os?: string;
  country?: string;
  city?: string;
  region?: string;
  search_term?: string;
  keyword_matched?: string;
  ad_position?: number;
  cost: number;
  converted: boolean;
  conversion_time?: string;
  conversion_value?: number;
  landing_page_url?: string;
  platform: 'google' | 'meta' | 'linkedin';
  external_click_id?: string;
  ad?: {
    id: string;
    name: string;
    headline?: string;
  };
}

export interface CampaignAudienceAssociation {
  id: string;
  campaign_id: string;
  audience_id: string;
  type: 'include' | 'exclude';
  bid_adjustment: number;
  priority: number;
  synced_to_platform: boolean;
  last_synced_at?: string;
  audience: {
    id: string;
    name: string;
    type: string;
    source: string;
    size: number;
    status: string;
  };
}

export interface LocationSuggestion {
  name: string;
  canonicalName: string;
  id: string;
  targetType: string;
  countryCode: string;
  searchTerm: string;
}

export interface PlatformPixel {
  id: string;
  name: string;
  lastFired?: string | null;
  isUnavailable?: boolean;
  type?: string;
  category?: string;
  selected: boolean;
}

export interface PlatformPixels {
  meta: PlatformPixel[];
  google: PlatformPixel[];
  linkedin: PlatformPixel[];
}

export const searchLocations = async (query: string): Promise<LocationSuggestion[]> => {
  if (!query || query.length < 2) return [];
  try {
    const response = await api.get(`/campaigns/locations/search?q=${encodeURIComponent(query)}`);
    return response.data?.data || [];
  } catch {
    return [];
  }
};

// ============================================
// Campaign recommendations (AI + native platform pull)
// ============================================
export interface RecommendationCard {
  id: string;
  type: string;
  title: string;
  description: string;
  source: 'ai' | 'google' | 'meta';
  priority?: 'high' | 'medium' | 'low';
  impact?: { impressions?: number; clicks?: number; costMicros?: string } | null;
  applyResourceName?: string | null;
}

export interface CampaignAnalysisResult {
  ai: RecommendationCard[];
  google: RecommendationCard[];
  meta: RecommendationCard[];
  linkedin: RecommendationCard[];
  generatedAt: string;
}

export const analyzeCampaign = async (campaignId: string): Promise<CampaignAnalysisResult> => {
  const response = await api.post(`/campaigns/${campaignId}/analyze`);
  if (!response.data?.success) throw new Error(response.data?.message || 'Falha ao analisar');
  return response.data.data;
};

export const applyGoogleRecommendation = async (campaignId: string, resourceName: string) => {
  const response = await api.post(`/campaigns/${campaignId}/recommendations/google/apply`, { resourceName });
  if (!response.data?.success) throw new Error(response.data?.message || 'Falha ao aplicar recomendação');
  return response.data.data;
};

/** Per-ad analysis (runs IA + Google scoped to the ad's ad_group). */
export const analyzeAd = async (campaignId: string, adId: string): Promise<CampaignAnalysisResult> => {
  const response = await api.post(`/campaigns/${campaignId}/ads/${adId}/analyze`);
  if (!response.data?.success) throw new Error(response.data?.message || 'Falha ao analisar');
  return response.data.data;
};

// ============================================
// Meta audience size estimate (Meta only)
// ============================================
export interface MetaAudienceEstimate {
  lower: number | null;
  upper: number | null;
  reason?: string;
  message?: string;
}

/**
 * Pull fresh metrics from Google/Meta/LinkedIn into our ad_metrics table for
 * this specific campaign. Returns per-platform sync result.
 */
export const syncCampaignMetrics = async (campaignId: string): Promise<{
  google?: any; meta?: any; linkedin?: any; errors?: string[]; syncedAt: string;
}> => {
  const response = await api.post(`/campaigns/${campaignId}/sync-metrics`);
  return response.data?.data ? { ...response.data.data, syncedAt: response.data.syncedAt, errors: response.data.errors } : { syncedAt: new Date().toISOString() };
};

export const estimateMetaAudience = async (params: {
  locations: Array<{ name: string; targetType?: string; countryCode?: string }>;
  age_min?: number;
  age_max?: number;
  genders?: string[];
}): Promise<MetaAudienceEstimate> => {
  const response = await api.post('/campaigns/meta-audience-estimate', params);
  return response.data?.data || { lower: null, upper: null };
};
